import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi, type SeatApplication, type SeatSource, type SeatStatus, type UserSeat } from '@/lib/api';
import { ChannelPill } from '@/lib/channel-icons';
import { SeatActionsCell, SeatStatusBadge, effectiveSeatStatus, expiryMeta, seatRemaining } from '@/components/seat-actions';
import { PageHeader } from '@/components/page-header';

const STATUS_OPTIONS: Array<{ value: SeatStatus | ''; label: string }> = [
  { value: '', label: '全部状态' },
  { value: 'active', label: '正常' },
  { value: 'suspended', label: '已暂停' },
  { value: 'expired', label: '已过期' },
];

const SOURCE_OPTIONS: Array<{ value: SeatSource | ''; label: string }> = [
  { value: '', label: '全部来源' },
  { value: 'default', label: '默认' },
  { value: 'admin', label: '管理员' },
  { value: 'application', label: '申请' },
  { value: 'manual', label: '手动' },
];

const PAGE_SIZE = 10;

export function SeatsPage() {
  return (
    <div className="space-y-6">
      <PageHeader title="席位" description="管理用户席位资格、审批申请，并为单个席位设置独立算力。" />
      <CreateSeatCard />
      <ApplicationsCard />
      <SeatsTable />
    </div>
  );
}

function CreateSeatCard() {
  const [user, setUser] = useState('');
  const defaultCredits = useDefaultSeatCreditsValue();
  const [credits, setCredits] = useState('');
  const [creditsTouched, setCreditsTouched] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (defaultCredits && !creditsTouched) setCredits(defaultCredits);
  }, [creditsTouched, defaultCredits]);
  const parsedCredits = Number(credits);
  const creditsValid = Number.isFinite(parsedCredits) && parsedCredits > 0;
  const createSeat = useMutation({
    mutationFn: () => adminApi.createSeat({
      user: user.trim(),
      credits: parsedCredits,
      note: note.trim(),
    }),
    onSuccess: () => {
      setNote('');
      toast.success('已创建席位');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">手动新增席位</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-[minmax(220px,1.5fr)_130px_minmax(200px,1.2fr)_auto] md:items-end">
        <Field label="用户手机号或 UUID">
          <Input value={user} onChange={(e) => setUser(e.target.value)} />
        </Field>
        <Field label="初始算力">
          <Input
            type="number"
            value={credits}
            onChange={(e) => {
              setCreditsTouched(true);
              setCredits(e.target.value);
            }}
            placeholder={defaultCredits || '默认算力'}
          />
        </Field>
        <Field label="原因">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Button disabled={!user.trim() || !creditsValid || !note.trim() || createSeat.isPending} onClick={() => createSeat.mutate()}>
          <Plus className="h-4 w-4 mr-1" /> 新增
        </Button>
      </CardContent>
    </Card>
  );
}

function ApplicationsCard() {
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['seat-applications', page, phoneFilter],
    queryFn: () => adminApi.listSeatApplications({
      status: 'pending',
      page,
      page_size: PAGE_SIZE,
      phone: phoneFilter,
    }),
  });
  const [creditsById, setCreditsById] = useState<Record<string, string>>({});
  const [noteById, setNoteById] = useState<Record<string, string>>({});
  const defaultCredits = useDefaultSeatCreditsValue();
  const creditValueFor = (app: SeatApplication) =>
    creditsById[app.id] ?? String(app.requested_credits ?? defaultCredits);
  const creditNumberFor = (app: SeatApplication) => Number(creditValueFor(app));
  const creditValidFor = (app: SeatApplication) => {
    const value = creditNumberFor(app);
    return Number.isFinite(value) && value > 0;
  };

  const approve = useMutation({
    mutationFn: (app: SeatApplication) =>
      adminApi.approveSeatApplication(app.id, {
        credits: creditNumberFor(app),
        admin_note: noteById[app.id] || '审批通过',
      }),
    onSuccess: () => toast.success('已通过该申请'),
  });

  const reject = useMutation({
    mutationFn: (app: SeatApplication) =>
      adminApi.rejectSeatApplication(app.id, { admin_note: noteById[app.id] || '审批拒绝' }),
    onSuccess: () => toast.success('已拒绝该申请'),
  });
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <CardTitle className="text-base">待审批申请</CardTitle>
          <div className="flex items-end gap-2">
            <Field label="按手机号筛选">
              <Input
                className="w-56"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="176..."
              />
            </Field>
            <Button
              variant="outline"
              onClick={() => {
                setPage(1);
                setPhoneFilter(phoneInput.trim());
              }}
            >
              查询
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data?.items.length === 0 && <p className="text-sm text-muted-foreground italic">暂无待审批申请</p>}
        {data && data.items.length > 0 && (
          <>
          <div className="max-h-[760px] overflow-y-auto pr-2">
          <div className="space-y-3">
            {data.items.map((app) => (
              <div key={app.id} className="rounded border border-border p-3">
                <div className="flex flex-wrap justify-between gap-2 text-sm">
                  <span>
                    {formatUser(app)}
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{app.user_id.slice(0, 8)}…</span>
                  </span>
                  <span className="text-muted-foreground">{new Date(app.created_at).toLocaleString('zh-CN')}</span>
                </div>
                <p className="mt-2 text-sm">{app.note || '无申请说明'}</p>
                <div className="mt-3 grid gap-2 md:grid-cols-[140px_1fr_auto_auto]">
                  <Input
                    type="number"
                    value={creditValueFor(app)}
                    onChange={(e) => setCreditsById({ ...creditsById, [app.id]: e.target.value })}
                  />
                  <Input
                    value={noteById[app.id] ?? ''}
                    onChange={(e) => setNoteById({ ...noteById, [app.id]: e.target.value })}
                    placeholder="审批备注"
                  />
                  <Button size="sm" onClick={() => approve.mutate(app)} disabled={!creditValidFor(app) || approve.isPending}>
                    <Check className="h-4 w-4 mr-1" /> 通过
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject.mutate(app)} disabled={reject.isPending}>
                    <X className="h-4 w-4 mr-1" /> 拒绝
                  </Button>
                </div>
              </div>
            ))}
          </div>
          </div>
          <Pager page={page} pageCount={pageCount} total={total} onPageChange={setPage} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function useDefaultSeatCreditsValue(): string {
  const { data } = useQuery({
    queryKey: ['settings', 'default-seat-credits'],
    queryFn: adminApi.getDefaultSeatCredits,
  });
  return data ? String(data.value) : '';
}

function SeatsTable() {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [status, setStatus] = useState<SeatStatus | ''>('');
  const [source, setSource] = useState<SeatSource | ''>('');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['seats', page, searchFilter, status, source],
    queryFn: () =>
      adminApi.listSeats({ page, page_size: PAGE_SIZE, q: searchFilter, status, source }),
  });
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['seats'] });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <CardTitle className="text-base">席位列表</CardTitle>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="手机号 / IP 名称 / 席位 ID">
              <Input
                className="w-64"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="手机号、IP 名称，或粘贴席位 ID"
              />
            </Field>
            <Field label="状态">
              <Select
                value={status || 'all'}
                onValueChange={(v) => {
                  setPage(1);
                  setStatus(v === 'all' ? '' : (v as SeatStatus));
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value || 'all'} value={o.value || 'all'}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="来源">
              <Select
                value={source || 'all'}
                onValueChange={(v) => {
                  setPage(1);
                  setSource(v === 'all' ? '' : (v as SeatSource));
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value || 'all'} value={o.value || 'all'}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Button
              variant="outline"
              onClick={() => {
                setPage(1);
                setSearchFilter(searchInput.trim());
              }}
            >
              查询
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data && (
          <>
          <Table wrapperClassName="max-h-[70vh]">
            <THead className="sticky top-0 z-10 bg-muted">
              <TR>
                <TH>用户</TH>
                <TH>席位</TH>
                <TH>渠道</TH>
                <TH>绑定 IP</TH>
                <TH>算力（已用 / 总额 · 剩余）</TH>
                <TH>有效期</TH>
                <TH>操作</TH>
              </TR>
            </THead>
            <TBody>
              {data.items.map((seat) => (
                <SeatRow key={seat.id} seat={seat} onChanged={invalidate} />
              ))}
            </TBody>
          </Table>
          {data.items.length === 0 && <p className="mt-3 text-sm text-muted-foreground italic">暂无席位</p>}
          <Pager page={page} pageCount={pageCount} total={total} onPageChange={setPage} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SeatRow({ seat, onChanged }: { seat: UserSeat; onChanged: () => void }) {
  const used = Number(seat.credits_used_cached);
  const granted = Number(seat.credits_granted);
  const remaining = seatRemaining(seat);
  const expiry = expiryMeta(seat.valid_until);

  return (
    <TR>
      <TD>
        <div>{formatUser(seat)}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{seat.user_id.slice(0, 8)}…</div>
      </TD>
      <TD>
        <div className="font-mono text-[10px]">{seat.id.slice(0, 8)}…</div>
        <div className="mt-0.5 flex items-center gap-1">
          <SeatStatusBadge status={effectiveSeatStatus(seat)} />
          <span className="text-[10px] text-muted-foreground">{seat.source}</span>
        </div>
      </TD>
      <TD><ChannelPill channel={seat.channel} /></TD>
      <TD>{seat.current_ip?.name ?? '未绑定'}</TD>
      <TD className="font-mono text-xs">
        {fmt(used)} / {fmt(granted)}
        <div className={`text-[10px] ${remaining <= 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
          剩余 {fmt(remaining)}
        </div>
      </TD>
      <TD className="text-xs">
        <div>{dateTime(seat.valid_from)}</div>
        <div className={expiry.className}>
          至 {dateTime(seat.valid_until)}
          {expiry.expired ? ' · 已过期' : expiry.soon ? ' · 即将到期' : ''}
        </div>
      </TD>
      <TD>
        <SeatActionsCell seat={seat} onChanged={onChanged} />
      </TD>
    </TR>
  );
}

function Pager({
  page,
  pageCount,
  total,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="mt-3 flex items-center justify-end gap-2 text-sm">
      <span className="text-muted-foreground">共 {total} 条，第 {page} / {pageCount} 页</span>
      <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
        上一页
      </Button>
      <Button size="sm" variant="outline" disabled={page >= pageCount} onClick={() => onPageChange(page + 1)}>
        下一页
      </Button>
    </div>
  );
}

function formatUser(row: { user_id: string; user?: { phone?: string | null } | null; user_phone?: string | null }): string {
  if (row.user?.phone) return `手机号：${row.user.phone}`;
  if (row.user_phone) return `手机号：${row.user_phone}`;
  return '手机号未设置';
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function dateTime(iso: string): string {
  return new Date(iso).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}
